import React, { useEffect, useState } from 'react';
import type { Mindmap, MindmapAttachment, MindmapViewMode } from './types';
import { VIEW_MODE_LABELS } from './types';
import { listMindmaps, getMindmap } from './mindmapApi';
import { leafNodeIds } from './treeUtils';

interface AttachSelectProps {
  value?: MindmapAttachment | null;
  onChange: (attachment: MindmapAttachment | null) => void;
}

const MODES: MindmapViewMode[] = ['before', 'during', 'after', 'fill_blank', 'blind_build'];

/**
 * Cho giáo viên đính kèm 1 sơ đồ tư duy vào bài tập + chọn chế độ xem.
 * Sinh snapshot (kèm cây node) để học sinh đọc trực tiếp không cần quyền sở hữu.
 */
export const MindmapAttachSelect: React.FC<AttachSelectProps> = ({ value, onChange }) => {
  const [list, setList] = useState<Mindmap[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listMindmaps()
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  const pick = async (mindmapId: string, viewMode: MindmapViewMode) => {
    if (!mindmapId) {
      onChange(null);
      return;
    }
    setBusy(true);
    try {
      const m = await getMindmap(mindmapId);
      const tree = m.tree;
      const hiddenNodeIds = viewMode === 'fill_blank' ? leafNodeIds(tree) : undefined;
      onChange({ mindmapId: String(m.id), title: m.title, viewMode, hiddenNodeIds, tree });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ fontSize: 12, color: '#6b7280' }}>Đang tải sơ đồ…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>🧠 Đính kèm sơ đồ tư duy (tuỳ chọn)</label>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af' }}>Chưa có sơ đồ nào. Hãy tạo ở mục “Sơ đồ tư duy”.</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={value?.mindmapId || ''}
            disabled={busy}
            onChange={(e) => pick(e.target.value, value?.viewMode || 'before')}
            style={sel}
          >
            <option value="">— Không đính kèm —</option>
            {list.map((m) => (
              <option key={m.id} value={m.id}>{m.title}</option>
            ))}
          </select>
          {value?.mindmapId && (
            <select
              value={value.viewMode}
              disabled={busy}
              onChange={(e) => pick(value.mindmapId, e.target.value as MindmapViewMode)}
              style={sel}
            >
              {MODES.map((mode) => (
                <option key={mode} value={mode}>{VIEW_MODE_LABELS[mode]}</option>
              ))}
            </select>
          )}
        </div>
      )}
      {value?.viewMode === 'fill_blank' && (
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          Đã tự động che {value.hiddenNodeIds?.length || 0} nhánh lá để học sinh điền.
        </div>
      )}
    </div>
  );
};

const sel: React.CSSProperties = { border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#fff', minWidth: 160 };

export default MindmapAttachSelect;
